import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  Table,
  TablePagination,
  CircularProgress,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControlLabel,
  Switch,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Delete, Edit, Add, FilterAltOff, CheckCircle, Cancel, PendingOutlined, AddPhotoAlternate, Close, Visibility, Campaign, OpenInNew, ConfirmationNumber, Group, Person, Flag, KeyboardArrowLeft, Smartphone, PublishedWithChanges } from '@mui/icons-material';
import { promotionsAPI, citiesAPI, promotionTypesAPI } from '../../services/api';
import { ApprovalStatus, ApprovalStatusLabels, APPROVAL_STATUS_FIELD } from '../../utils/enums';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';
import { canManagePromotions, canDeletePromotions } from '../../utils/roleUtils';
import browserGlobal from '../../utils/browserGlobal';

const TARGET_AUDIENCE_OPTIONS = [
  { value: 'All users', labelKey: 'promotions.targetAudienceAllUsers', defaultLabel: 'All users' },
  { value: 'Students', labelKey: 'promotions.targetAudienceStudents', defaultLabel: 'Students' },
  { value: 'Families', labelKey: 'promotions.targetAudienceFamilies', defaultLabel: 'Families' },
  { value: 'Workers', labelKey: 'promotions.targetAudienceWorkers', defaultLabel: 'Workers' },
];

const getTodayString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateTime = (date, time) => {
  if (!date) return '';
  
  // Handle date
  let datePart = '';
  if (date.includes(' ')) {
    datePart = date.split(' ')[0];
  } else if (date.includes('T')) {
    datePart = date.split('T')[0];
  } else {
    datePart = date;
  }

  // Handle time
  let timePart = '';
  if (time) {
    if (time.includes(' ')) {
      timePart = time.split(' ')[1];
    } else if (time.includes('T')) {
      timePart = time.split('T')[1];
    } else {
      timePart = time;
    }
    // Ensure time is in HH:mm format
    timePart = timePart.substring(0, 5);
  }

  return `${datePart}${timePart ? ' ' + timePart : ''}`;
};

function normalizeCommaSeparated(value) {
  if (typeof value === 'string') {
    if (!value.trim()) return [];
    return value.split(',').map((s) => s.replace(/‚/g, ',').trim()).filter(Boolean);
  }
  if (Array.isArray(value)) return value.map(s => typeof s === 'string' ? s.replace(/‚/g, ',') : s).filter(Boolean);
  return value || [];
}

const validateUploadedFile = (file) => {
  if (!file) return null;
  const name = file.name || '';
  const lowercaseName = name.toLowerCase();

  // Allowed extensions
  const allowedExtensions = ['.png', '.jpg', '.jpeg', '.svg'];
  const hasAllowedExt = allowedExtensions.some(ext => lowercaseName.endsWith(ext));
  if (!hasAllowedExt) {
    return 'Only .png, .jpg, .jpeg, and .svg files are allowed.';
  }

  // Block executables/installers explicitly
  const blockedExtensions = ['.exe', '.msi', '.bat', '.sh', '.cmd', '.com', '.scr', '.vbs', '.js'];
  const hasBlockedExt = blockedExtensions.some(ext => lowercaseName.endsWith(ext));
  if (hasBlockedExt) {
    return 'Installer and executable files (.exe, .msi, .bat, etc.) are blocked.';
  }

  // Blocks multi-extensions (abc.xyz.png)
  const dots = name.split('.').length - 1;
  if (dots > 1) {
    return 'Files with multiple extensions (e.g. abc.xyz.png) are not allowed.';
  }

  // Max size: 5 MB
  const maxSizeBytes = 5 * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return 'File size must be under 5 MB.';
  }

  return null;
};

/** Map API error message text to form field keys; unknown messages go to `_form`. */
const mapBackendMessageToFormErrors = (message) => {
  const msg = String(message || '').trim();
  if (!msg) return {};
  const l = msg.toLowerCase();
  if (l.includes('time')) return { promotionTime: msg };
  if (l.includes('date')) return { promotionDate: msg };
  if (l.includes('title')) return { title: msg };
  if (l.includes('description')) return { description: msg };
  if (l.includes('city')) return { cityId: msg };
  if (l.includes('image') || l.includes('picture') || l.includes('banner')) return { bannerImage: msg };
  if (l.includes('calltoaction') || l.includes('call to action')) return { callToAction: msg };
  return { _form: msg };
};

const extractImageUrls = (item) => {
  if (!item) return [];

  if (Array.isArray(item.pictures)) {
    return item.pictures
      .map((picture) =>
        typeof picture === 'string'
          ? picture
          : picture?.imageUrl || picture?.url || ''
      )
      .filter(Boolean);
  }

  if (Array.isArray(item.imageUrls)) {
    return item.imageUrls.filter(Boolean);
  }

  if (typeof item.imageUrls === 'string') {
    return item.imageUrls.split(',').map((x) => x.trim()).filter(Boolean);
  }

  if (typeof item.bannerImage === 'string') {
    return item.bannerImage.split(',').map((x) => x.trim()).filter(Boolean);
  }

  return [];
};

function getApprovalStatusChipColor(status) {
  if (status === ApprovalStatus.APPROVED) return 'success';
  if (status === ApprovalStatus.DECLINE) return 'error';
  if (status === ApprovalStatus.INACTIVE) return 'default';
  return 'warning';
}

function getApprovalDialogButtonColor(status) {
  if (status === ApprovalStatus.APPROVED) return 'success';
  if (status === ApprovalStatus.DECLINE) return 'error';
  return 'warning';
}

function getIsActiveFilterParam(statusFilter) {
  if (statusFilter === 'true') return true;
  if (statusFilter === 'false') return false;
  return undefined;
}


const isAddedByUser = (item) => {
  if (!item) return false;
  const type = String(
    item.addedByType ||
    item.AddedByType ||
    item.addedBy ||
    item.AddedBy ||
    ''
  ).toLowerCase().trim();
  return type === 'user' || type.includes('user');
};

const PromotionTableRow = ({
  promotion,
  promotionTypes,
  t,
  canAddEdit,
  canDelete,
  isSubmitting,
  onView,
  onViewData,
  onEdit,
  onDelete,
  onOpenApprovalDialog,
}) => {
  const promotionThumb = extractImageUrls(promotion)[0] || '';
  const promotionTypeId = promotion.promotionTypeId || promotion.PromotionTypeId;
  const promotionType = promotionTypes.find((type) => type.id === promotionTypeId);
  const rowActive = promotion.isActive === true || promotion.IsActive === true;

  return (
    <TableRow
      sx={{
        '&:hover': {
          backgroundColor: '#ebf4f4',
        },
      }}
    >
      <TableCell>
        {promotionThumb ? (
          <Box
            component="img"
            src={promotionThumb}
            alt=""
            sx={{
              width: 56,
              height: 56,
              objectFit: 'cover',
              borderRadius: 1,
              display: 'block',
              border: '1px solid #e2efef',
            }}
          />
        ) : (
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 1,
              border: '1px dashed #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: '#f9fafb',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          </Box>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="subtitle2">{promotion.title}</Typography>
      </TableCell>
      <TableCell>
        {promotionTypeId ? (
          <Chip
            label={promotionType?.title || promotionType?.name || `Type ID: ${promotionTypeId}`}
            size="small"
            sx={{ bgcolor: '#f3e8ff', color: '#6b21a8', fontWeight: 500 }}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        )}
      </TableCell>
      <TableCell>
        <Tooltip
          title={
            <Box sx={{ p: 0.5 }}>
              <Typography variant="body2">{promotion.description || '-'}</Typography>
            </Box>
          }
          placement="top"
          arrow
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 280 }}>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.4,
              }}
            >
              {promotion.description || '-'}
            </Typography>
          </Box>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Box>
          <Typography variant="body2" color="text.secondary">
            {formatDateTime(promotion.promotionDate, promotion.promotionTime)}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {promotion.ownerName || promotion.OwnerName || '-'}
        </Typography>
      </TableCell>
      <TableCell>
        {(promotion.addedByType || promotion.AddedByType || promotion.addedBy || promotion.AddedBy) ? (
          <Chip
            label={promotion.addedByType || promotion.AddedByType || promotion.addedBy || promotion.AddedBy}
            size="small"
            variant="outlined"
            color="info"
            sx={{ fontSize: '0.75rem', height: 24 }}
          />
        ) : (
          <Typography variant="body2" color="textSecondary">-</Typography>
        )}
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="textSecondary">
          {promotion.approvedByName || promotion.ApprovedByName || promotion.approvedBy || promotion.ApprovedBy || '-'}
        </Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={promotion.isActive ? t('common.active') : t('common.inactive')}
          color={promotion.isActive ? 'success' : 'default'}
          size="small"
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={ApprovalStatusLabels[promotion.approvalStatus] ?? promotion.approvalStatus}
              color={getApprovalStatusChipColor(promotion.approvalStatus)}
              size="small"
            />
          </Box>
          {promotion.approvalNote && (
            <Typography
              variant="caption"
              color={promotion.approvalStatus === ApprovalStatus.DECLINE ? 'error' : 'textSecondary'}
              sx={{ mt: 0.5, display: 'block', maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {t('promotions.notePrefix')}: {promotion.approvalNote}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <>
            <IconButton
              color="info"
              onClick={() => onView(promotion)}
              size="small"
              title={t('common.mobileView') || 'Mobile View'}
              disabled={isSubmitting}
            >
              <Smartphone />
            </IconButton>
            {isAddedByUser(promotion) ? (
              <IconButton
                color="info"
                onClick={() => onViewData ? onViewData(promotion) : onEdit(promotion, true)}
                size="small"
                title={t('common.view') || 'View'}
                disabled={isSubmitting}
              >
                <Visibility />
              </IconButton>
            ) : (
              <IconButton
                color="primary"
                onClick={() => onEdit(promotion)}
                size="small"
                title={t('common.edit')}
                disabled={isSubmitting}
              >
                <Edit />
              </IconButton>
            )}
            {canDelete && (
              <IconButton
                color="error"
                onClick={() => onDelete(promotion.id)}
                size="small"
                title={t('common.delete')}
                disabled={isSubmitting}
              >
                <Delete />
              </IconButton>
            )}
          </>
          {rowActive && (
            <IconButton
              color="warning"
              onClick={() => onOpenApprovalDialog(promotion, promotion.approvalStatus)}
              size="small"
              title={t('promotions.updateStatus') || 'Change Status'}
              disabled={isSubmitting}
            >
              <PublishedWithChanges />
            </IconButton>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
};

const PROMOTION_FIELD_ORDER = [
  'title',
  'description',
  'promotionDetails',
  'cityId',
  'promotionTypeId',
  'bannerImage',
  'discountText',
  'discountCoupon',
  'promotionDate',
  'promotionTime',
  'webUrl',
  'ownerName',
  'termsAndConditions',
  'callToAction',
  'targetAudience',
  'whatYouGet',
];

const Promotions = () => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const adminUser = getAdminUser();
  const canAddEdit = canManagePromotions(adminUser?.role);
  const canDelete = canDeletePromotions(adminUser?.role);

  const dialogContentRef = useRef(null);

  const scrollToField = (fieldKey) => {
    if (!dialogContentRef.current || !fieldKey) return;
    const container = dialogContentRef.current;
    const target =
      container.querySelector(`[data-field="${fieldKey}"]`) ||
      container.querySelector(`[name="${fieldKey}"]`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const inputEl = target.querySelector('input, textarea, select') || (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' ? target : null);
      if (inputEl) {
        setTimeout(() => {
          inputEl.focus({ preventScroll: true });
        }, 300);
      }
    }
  };

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState({ text: '', type: 'info' });
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [cities, setCities] = useState([]);
  const [promotionTypes, setPromotionTypes] = useState([]);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPromotion, setPreviewPromotion] = useState(null);
  const [activeBannerIndex, setActiveBannerIndex] = useState(0);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState('');
  const [approvalNote, setApprovalNote] = useState('');
  const [promotionToApprove, setPromotionToApprove] = useState(null);
  const [filters, setFilters] = useState({
    title: '',
    cityId: '',
    status: '' // '' = all, 'true' / 'false' → GET ?isActive=…
  });

  /** Debounced title sent as GET `title` (avoids a request on every keystroke). */
  const [debouncedTitle, setDebouncedTitle] = useState('');
  const debouncedTitleRef = useRef(debouncedTitle);
  useEffect(() => {
    debouncedTitleRef.current = debouncedTitle;
  }, [debouncedTitle]);

  const [formErrors, setFormErrors] = useState({});

  const previewImages = useMemo(() => {
    return extractImageUrls(previewPromotion);
  }, [previewPromotion]);

  const previewPromotionTypeTitle = useMemo(() => {
    if (!previewPromotion) return '';
    const typeId = previewPromotion.promotionTypeId || previewPromotion.PromotionTypeId;
    const match = promotionTypes.find((t) => t.id === typeId);
    return match?.title || match?.name || '';
  }, [previewPromotion, promotionTypes]);

  const previewWhatYouGetList = useMemo(() => {
    if (!previewPromotion) return [];
    return normalizeCommaSeparated(previewPromotion.whatYouGet || previewPromotion.WhatYouGet);
  }, [previewPromotion]);

  const handleOpenPreview = async (promotion) => {
    if (!promotion) return;
    setActiveBannerIndex(0);
    let fullPromotion = promotion;
    try {
      const detailId = promotion.id ?? promotion.promotionTypeId;
      let response;
      try {
        response = await promotionsAPI.getPromotion(detailId);
      } catch (err) {
        if (promotion.promotionTypeId) {
          response = await promotionsAPI.getPromotionByTypeId(promotion.promotionTypeId);
        }
      }
      if (response?.data?.payload) {
        fullPromotion = { ...promotion, ...response.data.payload };
      }
    } catch (error) {
      console.error('Error fetching promotion detail for preview:', error);
    }
    setPreviewPromotion(fullPromotion);
    setPreviewOpen(true);
  };

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    cityId: '',
    promotionTypeId: '',
    Pictures: [], // Stores new File objects
    existingBannerImages: [], // Stores existing image URLs
    picturesRemove: [],
    termsAndConditions: '',
    webUrl: '',
    webUrlButtonTitle: '',
    callToAction: '',
    discountText: '',
    discountCoupon: '',
    promotionDate: '',
    promotionTime: '',
    approvalStatus: 'PENDING',
    approvalNote: '',
    isActive: true,
    ownerName: '',
    approvedByName: '',
    targetAudience: '',
    whatYouGet: [],
    promotionDetails: ''
  });

  const showToast = (text, type = 'info') => {
    setToast({ text, type });
    setToastOpen(true);
  };

  useEffect(() => {
    const id = browserGlobal.setTimeout(() => {
      const next = (filters.title || '').trim();
      const prev = debouncedTitleRef.current;
      if (prev !== next) {
        setPage(0);
      }
      setDebouncedTitle(next);
    }, 400);
    return () => browserGlobal.clearTimeout(id);
  }, [filters.title]);

  useEffect(() => {
    setPage(0);
  }, [filters.status]);

  const fetchCities = useCallback(async () => {
    try {
      const response = await citiesAPI.getCities();
      if (response?.data?.success === true) {
        const list = response.data.payload || [];
        setCities(list);
        setFilters((prev) => {
          if (prev.cityId) return prev;
          if (list.length === 0) return prev;
          return { ...prev, cityId: list[0].id };
        });
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  }, []);

  const fetchPromotions = useCallback(async () => {
    const cityId = filters.cityId;
    if (!cityId) {
      setPromotions([]);
      setTotalCount(0);
      return;
    }
    setIsLoading(true);
    try {
      const isActiveParam = getIsActiveFilterParam(filters.status);
      const response = await promotionsAPI.getPromotions(cityId, page, rowsPerPage, {
        title: debouncedTitle || undefined,
        isActive: isActiveParam,
      });
      if (response?.data?.success === true) {
        const rawPayload = Array.isArray(response?.data?.payload) ? response.data.payload : [];
        const transformedPromotions = rawPayload.map((promotion) => ({
          ...promotion,
          ownerName: promotion.ownerName || promotion.OwnerName || '',
          approvedByName: promotion.approvedByName || promotion.ApprovedByName || promotion.approvedBy || promotion.ApprovedBy || '',
          addedByType: promotion.addedByType || promotion.AddedByType || promotion.addedBy || promotion.AddedBy || '',
        }));
        setPromotions(transformedPromotions);
        setTotalCount(response.data.totalRecord || 0);
      } else {
        setPromotions([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching promotions:', error);
      setPromotions([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [filters.cityId, filters.status, debouncedTitle, page, rowsPerPage]);

  const fetchPromotionTypes = useCallback(async () => {
    try {
      const response = await promotionTypesAPI.getPromotionTypes();
      if (response?.data?.success === true) {
        setPromotionTypes(Array.isArray(response.data.payload) ? response.data.payload : []);
      }
    } catch (error) {
      console.error('Error fetching promotion types:', error);
    }
  }, []);

  useEffect(() => {
    fetchCities();
    fetchPromotionTypes();
  }, [fetchCities, fetchPromotionTypes]);

  useEffect(() => {
    if (filters.cityId) {
      fetchPromotions();
    }
  }, [filters.cityId, fetchPromotions]);

  /** Refine current page if API ignores title/isActive; idempotent if API already filtered. */
  const visiblePromotions = useMemo(() => {
    const q = debouncedTitle.trim().toLowerCase();
    const hay = (v) => String(v ?? '').toLowerCase();
    const filtered = promotions.filter((promotion) => {
      if (q) {
        const matchTitle =
          hay(promotion.title).includes(q) ||
          hay(promotion.Title).includes(q) ||
          hay(promotion.description).includes(q) ||
          hay(promotion.Description).includes(q) ||
          hay(promotion.ownerName).includes(q) ||
          hay(promotion.approvedByName).includes(q) ||
          hay(promotion.addedByType).includes(q);
        if (!matchTitle) return false;
      }
      if (filters.status === 'true' && !(promotion.isActive === true || promotion.IsActive === true)) {
        return false;
      }
      if (filters.status === 'false' && !(promotion.isActive === false || promotion.IsActive === false)) {
        return false;
      }
      return true;
    });
    return filtered;
  }, [promotions, debouncedTitle, filters.status]);

  const handleOpen = async (promotion = null, isView = false) => {
    setViewMode(isView);
    setFormErrors({});
    if (promotion) {
      setIsLoading(true);
      try {
        const detailId = promotion.id ?? promotion.promotionTypeId;
        let response;
        try {
          response = await promotionsAPI.getPromotion(detailId);
        } catch (idError) {
          response = await promotionsAPI.getPromotionByTypeId(promotion.promotionTypeId ?? detailId);
        }
        const promotionData = response?.data?.payload || promotion;
        const detailImages = extractImageUrls(promotionData);
        const listImages = extractImageUrls(promotion);
        const mergedExistingImages = detailImages.length > 0 ? detailImages : listImages;

        // Keep row id for update API even if detail payload comes from different shape.
        setSelectedPromotion({
          ...promotionData,
          id: promotion.id ?? promotionData.id,
        });
        // Format date from DD/MM/YYYY to YYYY-MM-DD
        const formattedDate = promotionData.promotionDate
          ? promotionData.promotionDate.split('/').reverse().join('-')
          : '';
        // Get HH:MM from the time string
        const formattedTime = promotionData.promotionTime
          ? promotionData.promotionTime.split(':').slice(0, 2).join(':')
          : '';

        setFormData({
          title: promotionData.title || '',
          description: promotionData.description || '',
          cityId: promotionData.cityId || '',
          promotionTypeId: promotionData.promotionTypeId || '',
          Pictures: [],
          existingBannerImages: mergedExistingImages,
          picturesRemove: [],
          termsAndConditions:
            promotionData.termsAndConditions ||
            promotionData.TermsAndConditions ||
            '',
          webUrl: promotionData.webUrl || '',
          webUrlButtonTitle: promotionData.webUrlButtonTitle || '',
          callToAction: promotionData.callToAction || promotionData.CallToAction || '',
          discountText: promotionData.discountText || '',
          discountCoupon: promotionData.discountCoupon || '',
          promotionDate: formattedDate,
          promotionTime: formattedTime,
          approvalStatus: promotionData.approvalStatus || 'PENDING',
          approvalNote: promotionData.approvalNote || '',
          isActive: promotionData.isActive || false,
          ownerName: promotionData.ownerName || '',
          approvedByName: promotionData.approvedByName || '',
          targetAudience: promotionData.targetAudience || promotionData.TargetAudience || promotionData.targetedAudience || promotionData.TargetedAudience || '',
          whatYouGet: normalizeCommaSeparated(promotionData.whatYouGet || promotionData.WhatYouGet),
          promotionDetails: promotionData.promotionDetails || promotionData.PromotionDetails || ''
        });
      } catch (error) {
        console.error('Error fetching promotion details:', error);
        showToast(t('promotions.toastFetchError') || t('promotions.toastSaveError'), 'error');
      } finally {
        setIsLoading(false);
      }
    } else {
      setSelectedPromotion(null);
      setFormData({
        title: '',
        description: '',
        cityId: filters.cityId || '',
        promotionTypeId: '',
        Pictures: [],
        existingBannerImages: [],
        picturesRemove: [],
        termsAndConditions: '',
        webUrl: '',
        webUrlButtonTitle: '',
        callToAction: '',
        discountText: '',
        discountCoupon: '',
        promotionDate: '',
        promotionTime: '',
        approvalStatus: 'PENDING',
        approvalNote: '',
        isActive: true,
        ownerName: '',
        approvedByName: '',
        targetAudience: '',
        whatYouGet: [],
        promotionDetails: ''
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedPromotion(null);
    setViewMode(false);
    setFormErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name) {
      setFormErrors((prev) => ({ ...prev, [name]: '', _form: '' }));
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = [];
    let errorMsg = '';

    for (const file of files) {
      const error = validateUploadedFile(file);
      if (error) {
        errorMsg = error;
      } else {
        validFiles.push(file);
      }
    }

    if (errorMsg) {
      showToast(errorMsg, 'warning');
    }

    if (validFiles.length > 0) {
      setFormErrors((prev) => ({ ...prev, bannerImage: '', _form: '' }));
      setFormData((prev) => ({
        ...prev,
        Pictures: [...prev.Pictures, ...validFiles],
      }));
    }
    // Reset input value so selecting the same file again still triggers onChange
    e.target.value = '';
  };

  const handleRemoveImage = (index, isExisting = false) => {
    setFormData((prev) => {
      if (isExisting) {
        const removedUrl = prev.existingBannerImages[index];
        const updatedExisting = [...prev.existingBannerImages];
        updatedExisting.splice(index, 1);
        return {
          ...prev,
          existingBannerImages: updatedExisting,
          picturesRemove: [
            ...(prev.picturesRemove || []),
            removedUrl
          ].filter(Boolean)
        };
      } else {
        const updatedPictures = [...prev.Pictures];
        updatedPictures.splice(index, 1);
        return { ...prev, Pictures: updatedPictures };
      }
    });
  };

  const handleArrayFieldChange = (fieldName, index, newValue) => {
    setFormData((prev) => {
      const updatedArray = [...(prev[fieldName] || [])];
      updatedArray[index] = newValue;
      return {
        ...prev,
        [fieldName]: updatedArray,
      };
    });
    setFormErrors((prev) => ({
      ...prev,
      [fieldName]: '',
      _form: '',
    }));
  };

  const handleAddArrayItem = (fieldName) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), ''],
    }));
  };

  const handleRemoveArrayItem = (fieldName, index) => {
    setFormData((prev) => {
      const updatedArray = [...(prev[fieldName] || [])];
      updatedArray.splice(index, 1);
      return {
        ...prev,
        [fieldName]: updatedArray,
      };
    });
  };

  const handleSubmit = async () => {
    const nextErrors = {};
    if (!formData.title?.trim()) nextErrors.title = t('promotions.errFieldRequired');
    if (!formData.description?.trim()) nextErrors.description = t('promotions.errFieldRequired');
    if (!formData.cityId) nextErrors.cityId = t('promotions.errFieldRequired');
    if (!formData.promotionTypeId) nextErrors.promotionTypeId = t('promotions.errFieldRequired');
    if (!formData.promotionDate?.trim()) nextErrors.promotionDate = t('promotions.errFieldRequired');
    if (!formData.promotionTime?.trim()) nextErrors.promotionTime = t('promotions.errTimeRequiredShort');
    if (formData.promotionDate?.trim()) {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(formData.promotionDate.trim())) {
        nextErrors.promotionDate = t('promotions.errInvalidDate');
      } else if (!selectedPromotion && formData.promotionDate.trim() < getTodayString()) {
        nextErrors.promotionDate = t('promotions.errPastDate') || 'Past dates are not allowed. Please select today or a future date.';
      }
    }
    if (formData.promotionTime?.trim()) {
      const timePart = formData.promotionTime.trim().slice(0, 8);
      const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)(:([0-5]\d))?$/;
      if (!timeRegex.test(timePart)) nextErrors.promotionTime = t('promotions.errInvalidTime');
    }

    // Character count validation (max 200) for array string fields
    const arrayFields = ['whatYouGet'];
    let hasArrayError = false;
    arrayFields.forEach((field) => {
      const arr = formData[field] || [];
      const hasInvalidItem = arr.some((item) => (item || '').length > 200);
      if (hasInvalidItem) {
        nextErrors[field] = 'One or more items exceed 200 characters';
        hasArrayError = true;
      }
    });

    if (formData.targetAudience && formData.targetAudience.length > 200) {
      nextErrors.targetAudience = 'Target Audience cannot exceed 200 characters';
    }
    if (formData.callToAction && formData.callToAction.length > 100) {
      nextErrors.callToAction = 'Call to Action cannot exceed 100 characters';
    }

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      const firstErrorField = PROMOTION_FIELD_ORDER.find((key) => Boolean(nextErrors[key]));
      if (firstErrorField) {
        scrollToField(firstErrorField);
      }
      if (hasArrayError) {
        showToast("Please ensure all items in What You Get are within 200 characters.", 'warning');
      } else if (nextErrors.targetAudience) {
        showToast("Please ensure Targeted Audience is within 200 characters.", 'warning');
      } else if (nextErrors.callToAction) {
        showToast("Please ensure Call to Action is within 100 characters.", 'warning');
      }
      return;
    }

    setFormErrors({});
    setIsSubmitting(true);
    try {
      // Convert date from YYYY-MM-DD to DD/MM/YYYY for API
      let apiDate = '';
      if (formData.promotionDate && formData.promotionDate.includes('-')) {
        const [year, month, day] = formData.promotionDate.split('-');
        apiDate = `${day}/${month}/${year}`;
      } else {
        apiDate = formData.promotionDate || '';
      }
      // Format time as HH:mm
      const apiTime = formData.promotionTime ? formData.promotionTime.substring(0, 5) : '';

      // Create FormData for multipart submission
      const formDataToSend = new FormData();

      // Append all fields except special formatted ones. New promotions: omit approval fields (backend defaults to PENDING).
      Object.keys(formData).forEach((key) => {
        if (
          ['Pictures', 'pictures', 'existingBannerImages', 'picturesRemove', 'picturesKeep', 'promotionDate', 'promotionTime', 'promotionTypeId', 'targetAudience', 'whatYouGet', 'promotionDetails'].includes(key)
        ) {
          return;
        }
        if (!selectedPromotion && (key === 'approvalStatus' || key === 'approvalNote')) {
          return;
        }
        formDataToSend.append(key, formData[key] === null ? '' : formData[key]);
      });

     
      formDataToSend.append('promotionDate', apiDate);
      formDataToSend.append('promotionTime', apiTime);
      formDataToSend.append('PromotionTypeId', formData.promotionTypeId === '' || formData.promotionTypeId == null ? 0 : Number(formData.promotionTypeId));
      formDataToSend.append('promotionTypeId', formData.promotionTypeId === '' || formData.promotionTypeId == null ? 0 : Number(formData.promotionTypeId));
      
      // Pass new image uploads as pictures (only once)
      formData.Pictures.forEach((file) => {
        formDataToSend.append('pictures', file);
      });

      // Pass kept existing image URLs as picturesKeep
      const keepUrls = (formData.existingBannerImages || [])
        .map((u) => String(u).trim())
        .filter(Boolean);
      keepUrls.forEach((url) => {
        formDataToSend.append('picturesKeep', url);
      });

      // Pass removed image URLs as picturesRemove
      const picturesRemove = formData.picturesRemove || [];
      picturesRemove.forEach((url) => {
        formDataToSend.append('picturesRemove', url);
      });

      if (formData.targetAudience !== undefined) {
        formDataToSend.append('TargetAudience', String(formData.targetAudience || '').trim());
      }

      if (formData.callToAction !== undefined) {
        formDataToSend.append('CallToAction', String(formData.callToAction || '').trim());
      }

      if (formData.promotionDetails !== undefined) {
        formDataToSend.append('promotionDetails', String(formData.promotionDetails || '').trim());
      }

      const filteredWhatYouGet = (formData.whatYouGet || [])
        .map(item => (item || '').replace(/,/g, '‚').trim())
        .filter(Boolean);
      if (filteredWhatYouGet.length > 0) {
        filteredWhatYouGet.forEach((item) => {
          formDataToSend.append('whatYouGet', item);
          formDataToSend.append('WhatYouGet', item);
        });
      } else {
        formDataToSend.append('whatYouGet', '');
        formDataToSend.append('WhatYouGet', '');
      }

      let response;
      if (selectedPromotion) {
        formDataToSend.append('id', selectedPromotion.id);
        response = await promotionsAPI.updatePromotion(formDataToSend);
      } else {
        response = await promotionsAPI.createPromotion(formDataToSend);
      }

      if (response?.data?.success === true) {
        showToast(selectedPromotion ? t('promotions.toastUpdated') : t('promotions.toastAdded'), 'success');
        fetchPromotions();
        handleClose();
      } else {
        const apiMsg =
          response?.data?.errorMessage ||
          response?.data?.message ||
          (selectedPromotion ? t('promotions.toastUpdateError') : t('promotions.toastSaveError'));
        setFormErrors(mapBackendMessageToFormErrors(apiMsg));
      }
    } catch (error) {
      console.error('Error saving promotion:', error);
      const apiMsg =
        error?.response?.data?.errorMessage ||
        error?.response?.data?.message ||
        error?.message ||
        t('promotions.toastSaveError');
      setFormErrors(mapBackendMessageToFormErrors(apiMsg));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (browserGlobal.confirm(t('promotions.confirmDelete'))) {
      setIsLoading(true);
      try {
        await promotionsAPI.deletePromotion(id);
        fetchPromotions();
        showToast(t('promotions.toastDeleted'), 'success');
      } catch (error) {
        console.error('Error deleting promotion:', error);
        showToast(error?.response?.data?.errorMessage || t('promotions.toastDeleteError'), 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const openApprovalDialog = (promotion, status) => {
    const rowActive = promotion?.isActive === true || promotion?.IsActive === true;
    if (!rowActive) return;
    setPromotionToApprove(promotion);
    setApprovalStatus(status);
    setApprovalNote('');
    setApprovalDialogOpen(true);
  };

  const closeApprovalDialog = () => {
    setApprovalDialogOpen(false);
    setPromotionToApprove(null);
    setApprovalStatus('');
    setApprovalNote('');
  };

  const handleApproval = async () => {
    // Validate approval note for DECLINE status
    if (approvalStatus === ApprovalStatus.DECLINE && !approvalNote.trim()) {
      showToast(t('promotions.toastNoteReject'), 'warning');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await promotionsAPI.updatePromotionApproval({
        id: promotionToApprove.id,
        [APPROVAL_STATUS_FIELD]: approvalStatus,
        approvalNote: approvalNote.trim()
      });

      if (response?.data?.success === true) {
        showToast(t('promotions.toastApprovalUpdated'), 'success');
        closeApprovalDialog();
        fetchPromotions();
      } else {
        showToast(t('promotions.toastApprovalError') + ': ' + (response?.data?.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      console.error('Error updating approval status:', error);
      showToast(t('promotions.toastApprovalError') + ': ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClearFilters = () => {
    setFilters({
      title: '',
      cityId: cities.length > 0 ? cities[0].id : '',
      status: '' // all statuses visible (not an option in the status dropdown)
    });
    setPage(0);
  };

  const renderArrayField = (label, fieldName) => {
    const items = formData[fieldName] || [];
    return (
      <Grid item xs={12}>
        <Box sx={{ mt: 1, mb: 1, p: 2, border: '1px solid #e5e7eb', borderRadius: '12px', bgcolor: '#f9fafb' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151' }}>
              {label}
            </Typography>
            {!viewMode && (
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() => handleAddArrayItem(fieldName)}
                sx={{
                  color: '#0F3D3E',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { bgcolor: 'rgba(124, 58, 237, 0.04)' }
                }}
              >
                {t('contacts.addItem') || 'Add Item'}
              </Button>
            )}
          </Box>

          {items.length === 0 ? (
            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic', pl: 1 }}>
              {t('contacts.noItemsAdded') || 'No items added yet. Click "Add Item" to add.'}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {items.map((item, index) => {
                const isOverLimit = (item || '').length > 200;
                return (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      maxRows={4}
                      value={item}
                      onChange={(e) => handleArrayFieldChange(fieldName, index, e.target.value)}
                      placeholder={`Enter ${label.toLowerCase()} item`}
                      error={isOverLimit}
                      InputProps={{ readOnly: viewMode }}
                      helperText={
                        isOverLimit
                          ? `${t('contacts.charLimitExceeded') || 'Character limit exceeded:'} ${(item || '').length}/200`
                          : `${(item || '').length}/200`
                      }
                      FormHelperTextProps={{
                        sx: { color: isOverLimit ? 'error.main' : 'text.secondary', textAlign: 'right' }
                      }}
                    />
                    {!viewMode && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveArrayItem(fieldName, index)}
                        sx={{ mt: 0.5 }}
                      >
                        <Delete />
                      </IconButton>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </Grid>
    );
  };

  return (
    <Box p={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={2}
        flexDirection={{ xs: 'column', sm: 'row' }}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h4" color="primary">{t('promotions.title')}</Typography>
        {canAddEdit && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {t('promotions.add')}
          </Button>
        )}
      </Box>

      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: '#fff',
          borderRadius: 1,
          boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('promotions.filterCity')}</InputLabel>
              <Select
                value={filters.cityId}
                label={t('promotions.filterCity')}
                onChange={(e) => {
                  setFilters(prev => ({ ...prev, cityId: e.target.value }));
                  setPage(0);
                }}
              >
                {cities.map((city) => (
                  <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="promotions-status-filter-label" shrink>
                {t('promotions.filterStatus')}
              </InputLabel>
              <Select
                labelId="promotions-status-filter-label"
                displayEmpty
                value={filters.status}
                label={t('promotions.filterStatus')}
                renderValue={(selected) => {
                  if (selected === 'true') return t('common.active');
                  if (selected === 'false') return t('common.inactive');
                  return (
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {t('promotions.allStatusesHint')}
                    </Box>
                  );
                }}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, status: e.target.value }));
                  setPage(0);
                }}
              >
                <MenuItem value="">{t('common.all')}</MenuItem>
                <MenuItem value="true">{t('common.active')}</MenuItem>
                <MenuItem value="false">{t('common.inactive')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              size="small"
              fullWidth
              placeholder={t('promotions.searchTitle')}
              value={filters.title}
              onChange={(e) => setFilters(prev => ({ ...prev, title: e.target.value }))}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md="auto"
            sx={{ display: 'flex', alignItems: 'center', ml: { md: 'auto' } }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterAltOff />}
              onClick={handleClearFilters}
              sx={clearFilterButtonSx}
            >
              {t('common.clear')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress sx={{ color: '#0F3D3E' }} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            mt: 3,
            boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
            borderRadius: 2,
            '& .MuiTableCell-head': {
              backgroundColor: '#ebf4f4',
              color: '#0F3D3E',
              fontWeight: 600
            },
            '& .MuiTableRow-root:hover': {
              backgroundColor: '#ebf4f4'
            }
          }}
        >
          <Table sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 80 }}>{t('promotions.colImage')}</TableCell>
                <TableCell>{t('promotions.colTitle')}</TableCell>
                <TableCell>Promotion Type</TableCell>
                <TableCell>{t('promotions.colDescription')}</TableCell>
                <TableCell>{t('promotions.colDate')}</TableCell>
                <TableCell sx={{ minWidth: 130 }}>{t('contacts.colOwnerName')}</TableCell>
                <TableCell sx={{ minWidth: 130 }}>{t('contacts.colAddedByType')}</TableCell>
                <TableCell sx={{ minWidth: 130 }}>{t('contacts.colApprovedByName')}</TableCell>
                <TableCell>{t('promotions.colStatus')}</TableCell>
                <TableCell>{t('promotions.colApproval')}</TableCell>
                <TableCell>{t('promotions.colActions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visiblePromotions.map((promotion) => (
                <PromotionTableRow
                  key={promotion.id}
                  promotion={promotion}
                  promotionTypes={promotionTypes}
                  t={t}
                  canAddEdit={canAddEdit}
                  canDelete={canDelete}
                  isSubmitting={isSubmitting}
                  onView={(p) => handleOpenPreview(p)}
                  onViewData={(p) => handleOpen(p, true)}
                  onEdit={(p) => handleOpen(p, false)}
                  onDelete={handleDelete}
                  onOpenApprovalDialog={openApprovalDialog}
                />
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number.parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage={t('common.rowsPerPage')}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              '.MuiTablePagination-select': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-selectIcon': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-displayedRows': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-actions': {
                '& .MuiIconButton-root': {
                  color: '#0F3D3E',
                },
                '& .Mui-disabled': {
                  color: '#e9d5ff',
                }
              }
            }}
          />
        </TableContainer>
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" fullScreen={isMobileView}>
        <DialogTitle>{viewMode ? t('promotions.dialogView') : selectedPromotion ? t('promotions.dialogEdit') : t('promotions.dialogAdd')}</DialogTitle>
        <DialogContent ref={dialogContentRef}>
          <TextField
            margin="dense"
            name="title"
            data-field="title"
            label={`${t('promotions.fieldTitle')} *`}
            fullWidth
            value={formData.title}
            onChange={handleChange}
            error={Boolean(formErrors.title)}
            helperText={formErrors.title || undefined}
            InputProps={{ readOnly: viewMode }}
          />
          <TextField
            margin="dense"
            name="description"
            data-field="description"
            label={`${t('promotions.fieldDesc')} *`}
            fullWidth
            multiline
            rows={4}
            value={formData.description}
            onChange={handleChange}
            error={Boolean(formErrors.description)}
            helperText={formErrors.description || undefined}
            InputProps={{ readOnly: viewMode }}
          />
          <TextField
            margin="dense"
            name="promotionDetails"
            data-field="promotionDetails"
            label={`${t('promotions.fieldPromotionDetails')} (${t('common.optional')})`}
            fullWidth
            multiline
            rows={4}
            value={formData.promotionDetails}
            onChange={handleChange}
            error={Boolean(formErrors.promotionDetails)}
            helperText={formErrors.promotionDetails || undefined}
            InputProps={{ readOnly: viewMode }}
          />
          <FormControl fullWidth margin="dense" data-field="cityId" error={Boolean(formErrors.cityId)} sx={{ pointerEvents: viewMode ? 'none' : 'auto' }}>
            <InputLabel id="promotion-form-city-label">{`${t('promotions.filterCity')} *`}</InputLabel>
            <Select
              labelId="promotion-form-city-label"
              name="cityId"
              value={formData.cityId}
              label={`${t('promotions.filterCity')} *`}
              readOnly={viewMode}
              onChange={handleChange}
            >
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
              ))}
            </Select>
            {formErrors.cityId ? <FormHelperText>{formErrors.cityId}</FormHelperText> : null}
          </FormControl>
          <FormControl fullWidth margin="dense" data-field="promotionTypeId" error={Boolean(formErrors.promotionTypeId)} sx={{ pointerEvents: viewMode ? 'none' : 'auto' }}>
            <InputLabel id="promotion-type-select-label">Promotion Type *</InputLabel>
            <Select
              labelId="promotion-type-select-label"
              name="promotionTypeId"
              value={formData.promotionTypeId ?? ''}
              label="Promotion Type *"
              readOnly={viewMode}
              onChange={handleChange}
            >
              {promotionTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.title || type.name}
                </MenuItem>
              ))}
            </Select>
            {formErrors.promotionTypeId ? <FormHelperText>{formErrors.promotionTypeId}</FormHelperText> : null}
          </FormControl>
          <Grid item xs={12} data-field="bannerImage">
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#374151', mt: 1, mb: 0.5 }}>
              {t('promotions.fieldBanner')} ({t('common.optional')})
            </Typography>
            {formErrors.bannerImage ? (
              <Typography variant="caption" color="error" sx={{ display: 'block', mb: 1 }}>
                {formErrors.bannerImage}
              </Typography>
            ) : null}
            
            <Box sx={{ 
              p: 2, 
              border: '1px solid #e5e7eb', 
              borderRadius: '12px', 
              bgcolor: '#f9fafb',
              minHeight: '100px',
              mb: 2
            }}>
              <Grid container spacing={2}>
                {/* Existing Images */}
                {formData.existingBannerImages.map((url, idx) => (
                  <Grid item key={`existing-${idx}`}>
                    <Box sx={{ 
                      position: 'relative', 
                      width: 80, 
                      height: 80,
                      '&:hover .delete-btn': { opacity: 1 }
                    }}>
                      <img
                        src={url}
                        alt="Existing"
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover', 
                          borderRadius: '12px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                      {!viewMode && (
                        <IconButton
                          size="small"
                          className="delete-btn"
                          onClick={() => handleRemoveImage(idx, true)}
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            bgcolor: '#fff',
                            opacity: 0.8,
                            transition: 'all 0.2s',
                            '&:hover': { bgcolor: '#ef4444', color: '#fff', opacity: 1 },
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            p: 0.5
                          }}
                        >
                          <Close sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                ))}

                {/* New Selected Images */}
                {formData.Pictures.map((file, idx) => (
                  <Grid item key={`new-${idx}`}>
                    <Box sx={{ 
                      position: 'relative', 
                      width: 80, 
                      height: 80,
                      '&:hover .delete-btn': { opacity: 1 }
                    }}>
                      <img
                        src={URL.createObjectURL(file)}
                        alt="New"
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'cover', 
                          borderRadius: '12px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                      />
                      {!viewMode && (
                        <IconButton
                          size="small"
                          className="delete-btn"
                          onClick={() => handleRemoveImage(idx)}
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            bgcolor: '#fff',
                            opacity: 0.8,
                            transition: 'all 0.2s',
                            '&:hover': { bgcolor: '#ef4444', color: '#fff', opacity: 1 },
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            p: 0.5
                          }}
                        >
                          <Close sx={{ fontSize: 16 }} />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>
                ))}

                {/* Add Image Button */}
                {!viewMode && (
                  <Grid item>
                    <Button
                      component="label"
                      sx={{
                        width: 80,
                        height: 80,
                        border: '2px dashed #d1d5db',
                        borderRadius: '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6b7280',
                        textTransform: 'none',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#0F3D3E',
                          color: '#0F3D3E',
                          bgcolor: 'rgba(124, 58, 237, 0.04)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      <AddPhotoAlternate sx={{ fontSize: 28 }} />
                      <Typography variant="caption" sx={{ mt: 1, fontWeight: 500 }}>{t('common.add') || 'Add'}</Typography>
                      <input
                        type="file"
                        hidden
                        multiple
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Box>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} data-field="discountText">
              <TextField
                margin="dense"
                name="discountText"
                label={`${t('promotions.fieldDiscountText')} (${t('common.optional')})`}
                fullWidth
                value={formData.discountText}
                onChange={handleChange}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>
            <Grid item xs={12} sm={6} data-field="discountCoupon">
              <TextField
                margin="dense"
                name="discountCoupon"
                label={`${t('promotions.fieldDiscountCoupon')} (${t('common.optional')})`}
                fullWidth
                value={formData.discountCoupon}
                onChange={handleChange}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} data-field="promotionDate">
              <TextField
                margin="dense"
                name="promotionDate"
                label={`${t('promotions.fieldDate')} *`}
                type="date"
                fullWidth
                value={formData.promotionDate || ''}
                onChange={(e) => {
                  const dateValue = e.target.value;
                  setFormErrors((prev) => ({ ...prev, promotionDate: '', _form: '' }));
                  setFormData((prev) => ({
                    ...prev,
                    promotionDate: dateValue,
                  }));
                }}
                error={Boolean(formErrors.promotionDate)}
                helperText={formErrors.promotionDate || undefined}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: (selectedPromotion && formData.promotionDate && formData.promotionDate < getTodayString())
                    ? formData.promotionDate
                    : getTodayString(),
                }}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>
            <Grid item xs={12} sm={6} data-field="promotionTime">
              <TextField
                margin="dense"
                name="promotionTime"
                label={`${t('promotions.fieldTime')} *`}
                type="time"
                fullWidth
                value={formData.promotionTime || ''}
                onChange={(e) => {
                  const timeValue = e.target.value;
                  setFormErrors((prev) => ({ ...prev, promotionTime: '', _form: '' }));
                  setFormData((prev) => ({
                    ...prev,
                    promotionTime: timeValue,
                  }));
                }}
                error={Boolean(formErrors.promotionTime)}
                helperText={formErrors.promotionTime || undefined}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  step: 60 // Only hours and minutes
                }}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} data-field="webUrl">
              <TextField
                margin="dense"
                name="webUrl"
                label={`${t('promotions.fieldWebUrl')} (${t('common.optional')})`}
                fullWidth
                value={formData.webUrl}
                onChange={handleChange}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>
            <Grid item xs={12} sm={6} data-field="ownerName">
              <TextField
                margin="dense"
                name="ownerName"
                label={`${t('contacts.fieldOwnerName')} (${t('common.optional')})`}
                fullWidth
                value={formData.ownerName}
                onChange={handleChange}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>
          </Grid>
          <TextField
            margin="dense"
            name="termsAndConditions"
            data-field="termsAndConditions"
            label={`${t('promotions.fieldTermsAndConditions')} (${t('common.optional')})`}
            fullWidth
            value={formData.termsAndConditions}
            onChange={handleChange}
            InputProps={{ readOnly: viewMode }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} data-field="callToAction">
              <TextField
                margin="dense"
                name="callToAction"
                label={`${t('promotions.fieldCallToAction') || 'Call to Action'} (${t('common.optional')})`}
                fullWidth
                value={formData.callToAction || ''}
                onChange={handleChange}
                error={Boolean(formErrors.callToAction) || (formData.callToAction || '').length > 100}
                helperText={formErrors.callToAction || `${(formData.callToAction || '').length}/100`}
                FormHelperTextProps={{
                  sx: {
                    color: (formErrors.callToAction || (formData.callToAction || '').length > 100) ? 'error.main' : 'text.secondary',
                    textAlign: formErrors.callToAction ? 'left' : 'right',
                  },
                }}
                inputProps={{ maxLength: 100 }}
                InputProps={{ readOnly: viewMode }}
              />
            </Grid>
            <Grid item xs={12} sm={6} data-field="targetAudience">
              <FormControl fullWidth margin="dense" error={Boolean(formErrors.targetAudience)} sx={{ pointerEvents: viewMode ? 'none' : 'auto' }}>
                <InputLabel id="target-audience-select-label">{`${t('promotions.fieldTargetedAudience')} (${t('common.optional')})`}</InputLabel>
                <Select
                  labelId="target-audience-select-label"
                  name="targetAudience"
                  value={formData.targetAudience || ''}
                  label={`${t('promotions.fieldTargetedAudience')} (${t('common.optional')})`}
                  readOnly={viewMode}
                  onChange={handleChange}
                >
                  <MenuItem value="">
                    <em>{t('common.none') || 'None'}</em>
                  </MenuItem>
                  {TARGET_AUDIENCE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {t(opt.labelKey) || opt.defaultLabel}
                    </MenuItem>
                  ))}
                  {formData.targetAudience && !TARGET_AUDIENCE_OPTIONS.some((o) => o.value === formData.targetAudience) && (
                    <MenuItem value={formData.targetAudience}>
                      {formData.targetAudience}
                    </MenuItem>
                  )}
                </Select>
                {formErrors.targetAudience ? <FormHelperText>{formErrors.targetAudience}</FormHelperText> : null}
              </FormControl>
            </Grid>
          </Grid>
          <Box data-field="whatYouGet">{renderArrayField(t('promotions.fieldWhatYouGet'), 'whatYouGet')}</Box>
          {canAddEdit && (
            <Box mt={2} display="flex" gap={2}>
              <FormControlLabel
                control={
                  <Switch
                    name="isActive"
                    checked={formData.isActive}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        isActive: e.target.checked
                      }));
                    }}
                    sx={{
                      pointerEvents: viewMode ? 'none' : 'auto',
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#0F3D3E',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#0F3D3E',
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {t('common.active')}
                  </Typography>
                }
              />
            </Box>
          )}
          {formErrors._form ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formErrors._form}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>{viewMode ? t('common.close') || 'Close' : t('common.cancel')}</Button>
          {!viewMode && (
            <Button 
              onClick={handleSubmit} 
              variant="contained" 
              color="primary"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : null}
            >
              {selectedPromotion ? t('common.update') : t('common.create')}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approvalDialogOpen} onClose={closeApprovalDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('promotions.approvalDialogTitle')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>{t('common.status')}</InputLabel>
              <Select
                value={approvalStatus}
                label={t('common.status')}
                onChange={(e) => setApprovalStatus(e.target.value)}
              >
                <MenuItem value={ApprovalStatus.APPROVED}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle color="success" />
                    <span>{ApprovalStatusLabels[ApprovalStatus.APPROVED]}</span>
                  </Box>
                </MenuItem>
                <MenuItem value={ApprovalStatus.DECLINE}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Cancel color="error" />
                    <span>{ApprovalStatusLabels[ApprovalStatus.DECLINE]}</span>
                  </Box>
                </MenuItem>
                <MenuItem value={ApprovalStatus.PENDING}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PendingOutlined color="warning" />
                    <span>{ApprovalStatusLabels[ApprovalStatus.PENDING]}</span>
                  </Box>
                </MenuItem>
                <MenuItem value={ApprovalStatus.INACTIVE}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Cancel color="disabled" />
                    <span>{ApprovalStatusLabels[ApprovalStatus.INACTIVE]}</span>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label={approvalStatus === ApprovalStatus.DECLINE ? t('promotions.declineNote') : t('promotions.approvalNoteLabel')}
              name="approvalNote"
              value={approvalNote}
              onChange={(e) => setApprovalNote(e.target.value)}
              multiline
              rows={4}
              required={approvalStatus === ApprovalStatus.DECLINE}
              error={approvalStatus === ApprovalStatus.DECLINE && !approvalNote.trim()}
              helperText={approvalStatus === ApprovalStatus.DECLINE && !approvalNote.trim() ? t('promotions.declineNoteRequired') : ''}
              placeholder={approvalStatus === ApprovalStatus.DECLINE ? t('promotions.placeholderDecline') : t('promotions.placeholderOptional')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeApprovalDialog}>{t('common.cancel')}</Button>
          <Button
            onClick={handleApproval}
            variant="contained"
            color={getApprovalDialogButtonColor(approvalStatus)}
            disabled={isSubmitting || (approvalStatus === ApprovalStatus.DECLINE && !approvalNote.trim())}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? t('common.updating') : t('promotions.updateStatus')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Mobile View Mockup Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: '32px' },
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            maxHeight: '90vh',
            p: 0,
            m: 0,
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            position: 'relative',
          }}
        >
          {/* Floating Close Button */}
          <IconButton
            onClick={() => setPreviewOpen(false)}
            sx={{
              position: { xs: 'fixed', sm: 'absolute' },
              top: { xs: 10, sm: 10 },
              left: { xs: 'auto', sm: 'calc(50% + 155px)' },
              right: { xs: 10, sm: 'auto' },
              bgcolor: { xs: 'rgba(255,255,255,0.9)', sm: '#1e293b' },
              color: { xs: '#000', sm: '#ffffff' },
              '&:hover': {
                bgcolor: { xs: '#ffffff', sm: '#0f172a' }
              },
              zIndex: 2000,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <Close sx={{ fontSize: 20 }} />
          </IconButton>

          {/* Smartphone Frame Outer Bezel */}
          <Box
            sx={{
              width: { xs: '100%', sm: '290px' },
              height: { xs: '100vh', sm: '650px' },
              maxHeight: { xs: '100vh', sm: '85vh' },
              borderRadius: { xs: 0, sm: '32px' },
              border: { xs: 'none', sm: '6px solid #1e293b' },
              boxShadow: { xs: 'none', sm: '0 20px 40px -10px rgba(0,0,0,0.4)' },
              bgcolor: '#ffffff',
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {/* Inner Phone Content Area */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#ffffff',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {/* Scrollable Content Container */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  pb: '75px',
                  '&::-webkit-scrollbar': { width: '3px' },
                  '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: '3px' }
                }}
              >
                {/* Top Banner Image Section - Horizontally Scrollable */}
                <Box sx={{ width: '100%', height: '190px', position: 'relative', bgcolor: '#e2e8f0', overflow: 'hidden' }}>
                  {previewImages && previewImages.length > 0 ? (
                    <Box
                      onScroll={(e) => {
                        const width = e.currentTarget.clientWidth;
                        if (width > 0) {
                          const newIdx = Math.round(e.currentTarget.scrollLeft / width);
                          if (newIdx !== activeBannerIndex) setActiveBannerIndex(newIdx);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        width: '100%',
                        height: '100%',
                        overflowX: 'auto',
                        scrollSnapType: 'x mandatory',
                        '&::-webkit-scrollbar': { display: 'none' },
                        msOverflowStyle: 'none',
                        scrollbarWidth: 'none',
                      }}
                    >
                      {previewImages.map((imgUrl, idx) => (
                        <Box
                          key={idx}
                          component="img"
                          src={imgUrl}
                          alt={`Banner ${idx + 1}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            flexShrink: 0,
                            scrollSnapAlign: 'start',
                          }}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ width: '100%', height: '100%', bgcolor: '#cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>No Image Available</Typography>
                    </Box>
                  )}

                  {/* Back Button Floating Top-Left */}
                  <IconButton
                    onClick={() => setPreviewOpen(false)}
                    sx={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      bgcolor: '#ffffff',
                      color: '#1e293b',
                      width: 32,
                      height: 32,
                      boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
                      '&:hover': { bgcolor: '#f8fafc' },
                      zIndex: 10
                    }}
                  >
                    <KeyboardArrowLeft sx={{ fontSize: 20 }} />
                  </IconButton>

                  {/* Gradient Overlay */}
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: '80px',
                      background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0) 100%)',
                      pointerEvents: 'none',
                      zIndex: 2
                    }}
                  />

                  {/* Bottom-Left Badge & Title */}
                  <Box sx={{ position: 'absolute', bottom: 10, left: 12, right: 55, zIndex: 3, pointerEvents: 'none' }}>
                    <Typography
                      variant="caption"
                      sx={{
                        bgcolor: '#ea580c',
                        color: '#ffffff',
                        fontWeight: 700,
                        px: 1,
                        py: 0.25,
                        borderRadius: '10px',
                        display: 'inline-block',
                        fontSize: '0.62rem',
                        mb: 0.25,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                    >
                      {previewPromotionTypeTitle || previewPromotion?.discountText || 'New Customer Cashback'}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#ffffff',
                        fontWeight: 800,
                        fontSize: '1rem',
                        lineHeight: 1.2,
                        textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical'
                      }}
                    >
                      {previewPromotion?.title || 'Cashback'}
                    </Typography>
                  </Box>

                  {/* Image Counter Badge */}
                  {previewImages && previewImages.length > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 10,
                        right: 10,
                        bgcolor: 'rgba(15, 23, 42, 0.8)',
                        color: '#ffffff',
                        px: 0.8,
                        py: 0.2,
                        borderRadius: '10px',
                        fontSize: '0.62rem',
                        fontWeight: 700,
                        zIndex: 3
                      }}
                    >
                      {activeBannerIndex + 1}/{previewImages.length}
                    </Box>
                  )}
                </Box>

                {/* Main Body Content */}
                <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.75 }}>

                  {/* 1. WhatsApp / Callout Box */}
                  <Box
                    sx={{
                      bgcolor: '#fff8ec',
                      border: '1.2px solid #fed7aa',
                      borderRadius: '12px',
                      p: 1.25,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.25
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: '#ffedd5',
                        color: '#ea580c',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <Campaign sx={{ fontSize: 18 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: '#c2410c', fontSize: '0.78rem', lineHeight: 1.3 }}>
                        {previewPromotion?.callToAction || previewPromotion?.CallToAction || 'Write me with WhatsApp'}
                      </Typography>
                      {(previewPromotion?.whatsappText || previewPromotion?.phone) && (
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#ea580c', fontSize: '0.75rem' }}>
                          {previewPromotion?.whatsappText || previewPromotion?.phone}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* 2. What You Get Section */}
                  {(previewWhatYouGetList.length > 0 || previewPromotion?.whatYouGet || previewPromotion?.WhatYouGet) && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                        <Box sx={{ width: 3, height: 14, bgcolor: '#ea580c', borderRadius: '2px' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.82rem' }}>
                          What You Get
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {(previewWhatYouGetList.length > 0
                          ? previewWhatYouGetList
                          : [previewPromotion?.whatYouGet || previewPromotion?.WhatYouGet]
                        ).filter(Boolean).map((item, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              bgcolor: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              borderRadius: '12px',
                              p: 1.1,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <Box
                              sx={{
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                bgcolor: '#dcfce7',
                                color: '#16a34a',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0
                              }}
                            >
                              <CheckCircle sx={{ fontSize: 15 }} />
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.78rem' }}>
                              {item}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}

                  {/* 3. Description Section */}
                  {previewPromotion?.description && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                        <Box sx={{ width: 3, height: 14, bgcolor: '#ea580c', borderRadius: '2px' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.82rem' }}>
                          Description
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.76rem', lineHeight: 1.45, whiteSpace: 'pre-line' }}>
                        {previewPromotion.description}
                      </Typography>
                    </Box>
                  )}

                  {/* 4. Promotion Details Section */}
                  {(previewPromotion?.promotionDetails || previewPromotion?.PromotionDetails) && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                        <Box sx={{ width: 3, height: 14, bgcolor: '#ea580c', borderRadius: '2px' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.82rem' }}>
                          Promotion Details
                        </Typography>
                      </Box>
                      <Box sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', p: 1.25 }}>
                        <Typography variant="body2" sx={{ color: '#334155', fontSize: '0.76rem', lineHeight: 1.45 }}>
                          {previewPromotion.promotionDetails || previewPromotion.PromotionDetails}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* 5. Terms & Conditions Section */}
                  {(previewPromotion?.termsAndConditions || previewPromotion?.TermsAndConditions || previewPromotion?.webUrl) && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                        <Box sx={{ width: 3, height: 14, bgcolor: '#ea580c', borderRadius: '2px' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.82rem' }}>
                          Terms & Conditions
                        </Typography>
                      </Box>
                      <Box sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', p: 1.1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#475569',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '70%'
                          }}
                        >
                          {previewPromotion?.webUrl
                            ? previewPromotion.webUrl.replace(/^https?:\/\//i, '').split('/')[0]
                            : previewPromotion?.termsAndConditions || previewPromotion?.TermsAndConditions}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#1e3a8a', fontWeight: 800, fontSize: '0.76rem', cursor: 'pointer' }}>
                          View &gt;
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.62rem', display: 'block', mt: 0.5, lineHeight: 1.35 }}>
                        Offer subject to provider's terms. Welivin facilitates discovery only and is not party to this promotion.
                      </Typography>
                    </Box>
                  )}

                  {/* 6. Target Audience & Owner Name Card */}
                  {(previewPromotion?.targetAudience || previewPromotion?.TargetAudience || previewPromotion?.ownerName || previewPromotion?.OwnerName) && (
                    <Box sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '14px', p: 1.25, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                      {(previewPromotion?.targetAudience || previewPromotion?.TargetAudience) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#f3e8ff', color: '#7e22ce', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Group sx={{ fontSize: 16 }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.62rem', display: 'block', fontWeight: 500 }}>
                              Target Audience
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.76rem' }}>
                              {previewPromotion.targetAudience || previewPromotion.TargetAudience}
                            </Typography>
                          </Box>
                        </Box>
                      )}

                      {(previewPromotion?.targetAudience || previewPromotion?.TargetAudience) && (previewPromotion?.ownerName || previewPromotion?.OwnerName) && (
                        <Box sx={{ borderBottom: '1px solid #e2e8f0' }} />
                      )}

                      {(previewPromotion?.ownerName || previewPromotion?.OwnerName) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: '#fae8ff', color: '#c026d3', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Person sx={{ fontSize: 16 }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.62rem', display: 'block', fontWeight: 500 }}>
                              Owner Name
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#0f172a', fontSize: '0.76rem' }}>
                              {previewPromotion.ownerName || previewPromotion.OwnerName}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}

                  {/* 7. Report Promotion */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, pt: 0.5, pb: 0.5, color: '#64748b', cursor: 'pointer' }}>
                    <Flag sx={{ fontSize: 15 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                      Report Promotion
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Fixed Action Buttons at Bottom */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  bgcolor: '#ffffff',
                  borderTop: '1px solid #e2e8f0',
                  py: 0.75,
                  px: 1,
                  display: 'flex',
                  gap: 0.75,
                  zIndex: 100,
                  boxShadow: '0 -3px 8px rgba(0,0,0,0.04)'
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: '36px',
                    bgcolor: '#fff8ec',
                    border: '1.2px solid #facc15',
                    borderRadius: '10px',
                    px: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.75,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#fef3c7' }
                  }}
                >
                  <ConfirmationNumber sx={{ color: '#d97706', fontSize: 16 }} />
                  <Box sx={{ textAlign: 'left', lineHeight: 1 }}>
                    <Typography variant="caption" sx={{ color: '#b45309', fontSize: '0.58rem', display: 'block', fontWeight: 700 }}>
                      Copy Code
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#78350f', fontWeight: 900, fontSize: '0.72rem' }}>
                      {previewPromotion?.discountCoupon || 'GET50'}
                    </Typography>
                  </Box>
                </Box>

                <Box
                  sx={{
                    flex: 1,
                    height: '36px',
                    bgcolor: '#0f172a',
                    color: '#ffffff',
                    borderRadius: '10px',
                    px: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.75,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#1e293b' }
                  }}
                >
                  <OpenInNew sx={{ fontSize: 15 }} />
                  <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.72rem' }}>
                    Go To Website
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Dialog>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toast.type} sx={{ width: '100%' }}>
          {toast.text}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Promotions;
